import React from 'react';
import { connect } from 'react-redux';
import { Box, Tabs, Tab, withStyles } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';
import {
  fetchEventDetails,
  fetchEventDetailsLoading,
  updateEventLayout
} from '../../actions/event';
import LoadingAndPulse from '../shared/LoadingAndPulse';
import ErrorAndRefresh from '../shared/ErrorAndRefresh';
import Logger from '../../util/Logger';
import { handleErrorWithDialog } from '../../util/Errors';
import { showDialog } from '../../actions/dialog';
import Event from '../../models/Event';
import { fetchResponses, updateNoteworthyResponses } from '../../actions/responses';
import {
  arrowDownwardIcon,
  arrowUpwardIcon,
  CREATOR_REPORTS_ALL_RESPONSES,
  CREATOR_REPORTS_COMMENT_BY_SENTIMENT,
  CREATOR_REPORTS_DISTRIBUTION,
  CREATOR_REPORTS_HISTORICAL_TREND,
  CREATOR_REPORTS_NOTEWORTHY,
  CREATOR_REPORTS_SEGMENTATION,
  CREATOR_REPORTS_SENTIMENT_BY_RATING,
  CREATOR_REPORTS_STATISTICS,
  CREATOR_REPORTS_TOP_RESPONSES,
  CREATOR_REPORTS_AI_INSIGHTS,
  NETWORK_CODE_NOT_FOUND,
  PAGE_LINK_CUSTOMIZE_CREATOR_REPORT,
  PAGE_LINK_SEGMENTATION_HELP
} from '../../constants';
import './MainContent.css';


import AIInsightsFeaturebasedV1 from './AIInsightsFeaturebasedV1';
import { TopBarVisibility } from '../layout/TopBarVisibility';
import { getDefaultSections } from 'src/pages/CreatorReports/CreatorReports';
import Report from './Report';
import FlipMove from 'react-flip-move';
import store from '../../store/configureStore';
import { NavigationUtils } from '../../util/NavigationUtils';
import { BrowserChecker } from '../../BrowserChecker';
import { CreatorReportAnalytics } from '../../analytics/CreatorReportAnalytics';
import { EventUseCase } from '../../models/EventUseCase';
import { EventVisibilityType } from '../../models/EventVisibilityType';
import Response from '../../models/Response';
import _ from 'lodash';
import { AutoPageBreaker, StartingHeight } from './AutoPageBreaker';
import queryString from 'stringquery';
import { Switch } from '../shared/Switch';

class MainContent extends React.Component {

  constructor (props) {
    super(props);

    const { user } = props;
    if (!user) {
      Logger.assert('Missing user in DashboardReport component');
    }

    this.state = {};
  }

  componentDidMount () {
    this.loadEvent();
  }

  componentWillUnmount () {
    // Whenever we umount, clear out the store of event details
    store.dispatch(fetchEventDetailsLoading());
  }

  // This lives here because we can only fetch the event once we have a User object, and
  // we must be able to unconditionally fetch an event on page load.
  loadEvent = () => {
    // The event will be valid if we are passing a new one in from the parent, else it will
    // be null and this is the first fetch which we perform from the slug.
    const { event, match } = this.props;
    const slug = event ? Event.getSlug(event) : match.params.event;

    // Don't condition, if slug is bad we want to fail
    Logger.info(`Fetch info for event: ${slug}`);
    const { fetchEventDetails } = this.props;
    // Results must reflect the latest votes, response totals, and choices. Event
    // details include all of those derived values, so do not reuse the 10-minute
    // dashboard cache when opening a report.
    fetchEventDetails(slug, true);
  };

  loadResponses = () => {
    const { event, fetchResponses } = this.props;

    // If we have an event, we can fetch the responses list
    if (event) {
      fetchResponses(Event.getId(event));
    }
  };

  componentDidUpdate (prevProps) {
    const { event, eventError } = this.props;
    handleErrorWithDialog(eventError, prevProps.eventError, 'Unable to load Event');

    // on Event switched
    if (event && prevProps.event && Event.getId(event) !== Event.getId(prevProps.event)) {
      Logger.info('New event, load it');
      this.loadEvent();
      this.loadResponses();
    }

    if (event && !prevProps.event) {
      Logger.info('Initial event loaded, fetch responses');
      this.loadResponses();
    }
  }

  render () {
    const {
      location,
      user,
      responses,
      noteworthyPosts,
      loadingEvent,
      sections,
      eventError,
      event,
      stats,
      ideas,
      roster,
      collection,
      pulseStats,
      sentimentByRating,
      segmentation,
      editing,
      showDialog,
      doesHideLayoutsContain,
      addToHideLayouts,
      removeFromHideLayouts,
      onParentStateChanged,
      onActiveEventChanged,
      onExitEditMode,
      onEditModeClicked,
      updateEventLayout,
      updateNoteworthyResponses,
      updateState,
      disablePublish
    } = this.props;

    const {
      isNoteworthyResponsesVisuallyEnabled,
      isNoteworthyResponsesEnabled,
      isTopResponsesVisuallyEnabled,
      isTopResponsesEnabled,
      isStatsVisuallyEnabled,
      isStatsEnabled,
      isDistributionVisuallyEnabled,
      isDistributionEnabled,
      isHistoricalTrendVisuallyEnabled,
      isHistoricalTrendEnabled,
      isCommentsBySentimentVisuallyEnabled,
      isCommentsBySentimentEnabled,
      isSentimentByRatingVisuallyEnabled,
      isSentimentByRatingEnabled,
      isSegmentationVisuallyEnabled,
      isSegmentationEnabled,
      isAllResponsesVisuallyEnabled,
      isAllResponsesEnabled,
      isAIInsightsVisuallyEnabled,
      isAIInsightsEnabled,
      isPdfRender,
      pdfRatingDistributionByIdeaId,
      pdfCommentsByIdeaId
    } = this.props;

    // If we have an error, show it
    if (eventError) {
      return (
        <div className="fill-page">
          <ErrorAndRefresh
            error="Unable to load Event"
            // If its a 404, hide the refresh button cause it doesn't exist anyway.
            onRefresh={ eventError.code === NETWORK_CODE_NOT_FOUND ? null : this.loadEvent } />
        </div>
      );
    } else if (loadingEvent || !event) {
      // Else if we don't have an event or we are still loading
      return (
        <div className="fill-page">
          <LoadingAndPulse
            size={ 20 }
            color="#3D9AE2"
            whatIsLoading="" />
        </div>
      );
    } else {
      const { activeAITab } = this.state;
      
      return (
        <Report
          location={location}
          user={user}
          event={event}
          collection={collection}
          responses={responses}
          stats={stats}
          roster={roster}
          ideas={ideas}
          sections={sections}
          noteworthyPosts={noteworthyPosts}
          pulseStats={pulseStats}
          sentimentByRating={sentimentByRating}
          segmentation={segmentation}
          isPdfRender={isPdfRender}
          pdfRatingDistributionByIdeaId={pdfRatingDistributionByIdeaId}
          pdfCommentsByIdeaId={pdfCommentsByIdeaId}
          disablePublish={disablePublish}
          editing={editing}
          showDialog={showDialog}
          updateEventLayout={updateEventLayout}
          updateNoteworthyResponses={updateNoteworthyResponses}
          onActiveEventChanged={onActiveEventChanged}
          onExitEditMode={onExitEditMode}
          onEditModeClicked={onEditModeClicked}
          doesHideLayoutsContain={doesHideLayoutsContain}
          addToHideLayouts={addToHideLayouts}
          removeFromHideLayouts={removeFromHideLayouts}
          onParentStateChanged={onParentStateChanged}
          updateState={updateState}
          isNoteworthyResponsesVisuallyEnabled={isNoteworthyResponsesVisuallyEnabled}
          isNoteworthyResponsesEnabled={isNoteworthyResponsesEnabled}
          isTopResponsesVisuallyEnabled={isTopResponsesVisuallyEnabled}
          isTopResponsesEnabled={isTopResponsesEnabled}
          isStatsVisuallyEnabled={isStatsVisuallyEnabled}
          isStatsEnabled={isStatsEnabled}
          isDistributionVisuallyEnabled={isDistributionVisuallyEnabled}
          isDistributionEnabled={isDistributionEnabled}
          isHistoricalTrendVisuallyEnabled={isHistoricalTrendVisuallyEnabled}
          isHistoricalTrendEnabled={isHistoricalTrendEnabled}
          isCommentsBySentimentVisuallyEnabled={isCommentsBySentimentVisuallyEnabled}
          isCommentsBySentimentEnabled={isCommentsBySentimentEnabled}
          isSentimentByRatingVisuallyEnabled={isSentimentByRatingVisuallyEnabled}
          isSentimentByRatingEnabled={isSentimentByRatingEnabled}
          isSegmentationVisuallyEnabled={isSegmentationVisuallyEnabled}
          isSegmentationEnabled={isSegmentationEnabled}
          isAllResponsesVisuallyEnabled={isAllResponsesVisuallyEnabled}
          isAllResponsesEnabled={isAllResponsesEnabled}
          isAIInsightsVisuallyEnabled={isAIInsightsVisuallyEnabled}
          isAIInsightsEnabled={isAIInsightsEnabled}
        />
      );
    }
  }
}



export const EnableSectionUpsell = ({ content }) => (
  <div className="col px-0">
    <div className="row no-gutters h-100">
      <div className="my-auto h-100">
        { content }
      </div>
    </div>
  </div>
);

export const EditSwitch = ({ id, checked, onChange }) => {
  return (
    <div className="row no-gutters mb-3 edit-switch">
      <div className="col" />
      <div className="my-auto">
        <Switch id={ id } checked={ checked } text="&nbsp;" onChange={ onChange } />
      </div>
    </div>
  );
};

export const EditArrowSection = ({ name, sections, onSectionsSet, editing, index }) => {
  if (!editing) {
    return null;
  }

  if (!name) {
    Logger.assert('Section needs a name:', index);
  }

  if (name.indexOf(' ') >= 0) {
    Logger.assert(`Names cannot contains spaces ${name}`);
  }

  const upDisabled = index === 0;
  const downDisabled = index >= sections.length - 1;

  const onUpClicked = ($event) => {
    $event.preventDefault();

    if (!upDisabled) {
      const newSections = [];
      for (let section of sections) {
        newSections.push(section);
      }

      const currentSection = newSections[index];
      newSections[index] = newSections[index - 1];
      newSections[index - 1] = currentSection;
      Logger.info(`Move section ${name} from ${index} to ${index - 1}`);
      onSectionsSet(newSections);

      CreatorReportAnalytics.customizeMoveClicked(name, 'up');
    }
  };

  const onDownClicked = ($event) => {
    $event.preventDefault();

    if (!downDisabled) {
      const newSections = [];
      for (let section of sections) {
        newSections.push(section);
      }

      const currentSection = newSections[index];
      newSections[index] = newSections[index + 1];
      newSections[index + 1] = currentSection;
      Logger.info(`Move section ${name} from ${index} to ${index + 1}`);
      onSectionsSet(newSections);

      CreatorReportAnalytics.customizeMoveClicked(name, 'down');
    }
  };

  return (
    <div className="col-1 switcher-arrows">
      <div className={ `${upDisabled ? 'invisible' : 'clickable'}` } onClick={ onUpClicked }>
        <i className="material-icons">{ arrowUpwardIcon }</i>
      </div>
      <div className={ `${downDisabled ? 'invisible' : 'clickable'}` } onClick={ onDownClicked }>
        <i className="material-icons">{ arrowDownwardIcon }</i>
      </div>
    </div>
  );
};

const styles = createStyles(theme => ({
  newBadge: {
    position: 'absolute',
    top: 4,
    right: -20,
    backgroundColor: theme.palette.secondary.main,
    color: '#fff',
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '10px',
    lineHeight: '12px',
    textTransform: 'uppercase',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    animation: 'pulse 2s infinite',
    '@keyframes pulse': {
      '0%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.05)' },
      '100%': { transform: 'scale(1)' }
    }
  },
  tabWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center'
  }
}));

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    loadingEvent: state.event.loading,
    eventError: state.event.error
  };
};

export default connect(mapStateToProps, {
  showDialog,
  fetchEventDetails,
  fetchResponses,
  updateEventLayout,
  updateNoteworthyResponses
})(MainContent);
